// jsonFetch.ts — Fetch an application/property JSON the way the rest of the app
// does: either directly, or POSTed through the conversion server's
// `loadjsonpostfix/proxy` when `postFixServerToken` is on (which is how deployments
// that keep the storage private reach their files).
//
// The same sequence is inlined in Cadviewer.Context.fetchPlanJson and in
// PlanJsonRightPanel.fetchSearchJson; new callers should use this helper rather
// than adding a third copy. Keep it in sync with those two if the transport
// changes.

import cleanKeys from "./object";

export interface JsonFetchConfig {
  ServerBackEndUrl: string;
  postFixServerToken?: boolean;
  bearerToken?: string;
}

/**
 * GET (or proxy-POST) a CADViewer JSON file and return its `data` payload with
 * keys cleaned, matching what the contexts store in state.
 */
export async function fetchCadviewerJson<T = any>(
  config: JsonFetchConfig,
  url: string
): Promise<T> {
  const base = config.ServerBackEndUrl || "";
  const endpoint = base.endsWith("/")
    ? "loadjsonpostfix/proxy"
    : "/loadjsonpostfix/proxy";

  const usesProxy = Boolean(config.postFixServerToken);

  let headers: Record<string, string> | undefined;
  if (usesProxy) headers = { "Content-Type": "application/json" };
  if (config.bearerToken) {
    headers = { ...(headers ?? {}), Authorization: `${config.bearerToken}` };
  }

  const response = await fetch(usesProxy ? base + endpoint : url, {
    method: usesProxy ? "POST" : "GET",
    headers,
    body: usesProxy ? JSON.stringify({ url }) : undefined,
    redirect: "follow",
  });

  if (!response.ok) {
    throw new Error(`Failed to load ${url}: HTTP ${response.status}`);
  }

  const json = await response.json();
  return cleanKeys(json?.data ?? json) as T;
}
